import React, { useState, useEffect } from 'react';
import { inviteCompanyUser, deleteCompanyUser, getCompanyUsers } from '../utils/companyApi';
import toastr from 'toastr';

/**
 * CompanyUsers - Company user management interface
 * Allows company admins to invite and manage users
 */
const CompanyUsers = ({ users, companyID, mspID, companyConfig, isAdmin, isSidebarOpen, onUsersChange }) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [email, setEmail] = useState('');
  const [accessLevel, setAccessLevel] = useState('member');
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState('');
  const [loadedUsers, setLoadedUsers] = useState(users || []);
  const [loadingUsers, setLoadingUsers] = useState(!users);

  // Load users if not provided as prop
  useEffect(() => {
    if (!users && companyID) {
      loadUsers();
    }
  }, [companyID, users]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const result = await getCompanyUsers(companyID);
      setLoadedUsers(result.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toastr.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) {
      toastr.warning('Please enter an email address');
      return;
    }

    if (!mspID) {
      toastr.error('MSP ID not found. Please refresh the page.');
      console.error('Missing mspID - cannot invite user');
      return;
    }

    setLoading(true);
    try {
      await inviteCompanyUser(mspID, companyID, email.trim(), accessLevel);
      toastr.success('User invited successfully! They will receive an email with instructions.');
      setShowInviteModal(false);
      setEmail('');
      setAccessLevel('member');
      
      // Refresh user list
      if (onUsersChange) {
        onUsersChange();
      } else {
        loadUsers();
      }
    } catch (error) {
      console.error('Invite error:', error);
      toastr.error(error.message || 'Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userEmail) => {
    if (!window.confirm(`Are you sure you want to remove ${userEmail} from the company?`)) {
      return;
    }
    
    setDeleteLoading(userEmail);
    try {
      await deleteCompanyUser(companyID, userEmail);
      toastr.success('User removed successfully');
      
      // Refresh user list
      if (onUsersChange) {
        onUsersChange();
      } else {
        loadUsers();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toastr.error(error.message || 'Failed to remove user');
    } finally {
      setDeleteLoading('');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { class: 'bg-success', text: 'Active' },
      'pending_password': { class: 'bg-warning text-dark', text: 'Pending Password' },
      'invited': { class: 'bg-secondary', text: 'Invited' },
      'unconfirmed': { class: 'bg-info', text: 'Unconfirmed' },
      'pending': { class: 'bg-secondary', text: 'Pending' },
      'deleted': { class: 'bg-danger', text: 'Deleted' },
      'error': { class: 'bg-dark', text: 'Error' }
    };
    const config = statusMap[status] || { class: 'bg-secondary', text: status };
    return (
      <span className={`badge ${config.class}`} style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
        {config.text}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      'admin': { class: 'bg-primary', text: 'Admin' },
      'member': { class: 'bg-secondary', text: 'Member' },
      'viewer': { class: 'bg-info', text: 'Viewer' },
      'owner': { class: 'bg-danger', text: 'Owner' }
    };
    const config = roleMap[role] || { class: 'bg-secondary', text: role };
    return (
      <span className={`badge ${config.class}`} style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
        {config.text}
      </span>
    );
  };

  // Determine which users array to use
  const displayUsers = users || loadedUsers;
  const currentIsAdmin = isAdmin !== undefined ? isAdmin : true; // Default to true if not provided

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Company Users</h2>
          <p className="text-muted mb-0">
            Manage users for {companyConfig?.companyName || 'your company'}
          </p>
        </div>
        {currentIsAdmin && (
          <button 
            className="btn btn-primary" 
            onClick={() => setShowInviteModal(true)}
          >
            <i className="fas fa-plus me-2"></i>Invite User
          </button>
        )}
      </div>

      {/* Admin Notice */}
      {!currentIsAdmin && (
        <div className="alert alert-info mb-4">
          <i className="fas fa-info-circle me-2"></i>
          You are viewing company users. Only administrators can invite or remove users.
        </div>
      )}

      {/* Loading State */}
      {loadingUsers ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3">Loading users...</p>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            {displayUsers.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-users text-muted" style={{ fontSize: '48px' }}></i>
              <p className="text-muted mt-3 mb-0">No users found</p>
              {currentIsAdmin && (
                <button 
                  className="btn btn-primary mt-3" 
                  onClick={() => setShowInviteModal(true)}
                >
                  Invite First User
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Access Level</th>
                    <th>Status</th>
                    <th>Invited</th>
                    {currentIsAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {displayUsers.map(user => (
                    <tr key={user.email}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div 
                            className="me-2" 
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#342EAD',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '14px'
                            }}
                          >
                            {user.email?.[0]?.toUpperCase() || 'U'}
                          </div>
                          {user.email}
                        </div>
                      </td>
                      <td>{getRoleBadge(user.accessLevel || user.role || 'member')}</td>
                      <td>{getStatusBadge(user.status || 'active')}</td>
                      <td>
                        {user.invitedAt 
                          ? new Date(user.invitedAt).toLocaleDateString()
                          : new Date(user.createdAt).toLocaleDateString()
                        }
                      </td>
                      {currentIsAdmin && (
                        <td>
                          {user.accessLevel !== 'owner' && user.role !== 'owner' && (
                            <button 
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(user.email)}
                              disabled={deleteLoading === user.email}
                            >
                              {deleteLoading === user.email ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1"></span>
                                  Removing...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-trash me-1"></i>
                                  Remove
                                </>
                              )}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => {
            if (e.target.className.includes('modal fade show')) {
              setShowInviteModal(false);
            }
          }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-user-plus me-2"></i>
                  Invite Company User
                </h5>
                <button 
                  type="button"
                  className="btn-close" 
                  onClick={() => setShowInviteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Email Address <span className="text-danger">*</span>
                  </label>
                  <input 
                    type="email" 
                    className="form-control" 
                    placeholder="user@example.com"
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                  />
                  <div className="form-text">
                    The user will receive an email invitation to join your company
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Access Level</label>
                  <select 
                    className="form-select" 
                    value={accessLevel} 
                    onChange={(e) => setAccessLevel(e.target.value)}
                  >
                    <option value="viewer">Viewer - Read-only access</option>
                    <option value="member">Member - Standard access</option>
                    <option value="admin">Admin - Full access + user management</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button"
                  className="btn btn-secondary" 
                  onClick={() => setShowInviteModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  className="btn btn-primary" 
                  onClick={handleInvite} 
                  disabled={loading || !email.trim()}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending Invitation...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-2"></i>
                      Send Invitation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyUsers;
